import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { ArrowLeft, ChevronRight, ShoppingBag } from 'lucide-react';

export const OrdersPage = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const response = await api().get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        <h1 className="font-heading font-bold text-4xl uppercase text-white mb-8">My Orders</h1>
        
        {orders.length === 0 ? (
          <div className="stat-card p-8 text-center">
            <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No orders yet</p>
            <Link to="/marketplace" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
              Browse marketplace
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="stat-card p-4 flex items-center gap-4 hover:border-white/10 transition-colors"
                data-testid={`order-${order.id}`}
              >
                <div className="w-16 h-20 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                  {order.card_details?.image_url ? (
                    <img src={order.card_details.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600 font-heading font-bold">
                      {order.card_details?.player_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{order.card_details?.player_name}</p>
                  <p className="text-sm text-zinc-400">{order.card_details?.year} {order.card_details?.set_name}</p>
                  <span className={`badge mt-2 ${order.status === 'paid' ? 'badge-emerald' : 'badge-amber'}`}>
                    {order.status}
                  </span>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-emerald-400">${order.total?.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                  <ChevronRight className="w-5 h-5 text-zinc-500 ml-auto mt-2" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default OrdersPage;
